<template>
  <div class='container container-padded-small order'>

    <h2 v-if="order.status == 'Pledge'">Confirm Selection</h2>
    <h2 v-else-if="!admin && order.can_edit_preorder">Confirm Order Details</h2>
    <h2 v-else>Order {{ order.uid }}</h2>

    <div class='line' />

    <p v-if="!admin && order.can_edit_preorder" style="margin-top: 10px; margin-bottom: 0px;">
      Please confirm your shipping address and accessories below.
    </p>

    <form @submit.prevent='validate_order' :class='{ errors: $v.$error }' novalidate>
      <div class='order-left'>
        <div v-if="order.status == 'Pledge'">
          Thanks for supporting Arsenal 2! Please complete the below selections to finalize your pledge and ship your reward.
        </div>

        <!-- Normal edit -->
        <Products
          v-if="order.status != 'Pledge' && (admin || !order.can_edit_preorder)"
          :order="order"
          :prices="prices"
          :$v="$v"
          :hide_pro="hide_pro"
          :hide_standard="hide_standard"
          :continental_usa="continental_usa"
        />

        <!-- Pledge confirmation -->
        <PledgeProducts
          v-if="order.status == 'Pledge'" :order="order"
          :prices="prices" :$v="$v"
          :hide_pro="hide_pro"
          :hide_standard="hide_standard"
          :continental_usa="continental_usa"
        />

        <!-- Pre-order confirmation -->
        <PreorderProducts
          v-if="!admin && order.can_edit_preorder"
          :order="order"
          :prices="prices"
          :$v="$v"
          :hide_pro="hide_pro"
          :hide_standard="hide_standard"
          :hide_pro_upgrade="hide_pro_upgrade"
          :continental_usa="continental_usa"
        />

        <section class='customer-info'>
          <h3>CUSTOMER INFORMATION</h3>
          <div class='row'>
            <div class='col s12 l7'>
              <input type='email' name='email' v-model.trim='order.email' placeholder='Email'>
              <div class='error' v-if='!$v.order.email.required'>Email is required</div>
              <div class='error' v-if='!$v.order.email.email'>Email is not the right format</div>
            </div>

            <div class='col s12 l5'>
              <input type='tel' name='phone' v-model.trim='order.phone_number' placeholder='Phone Number'>
              <div class='error' v-if='!$v.order.phone_number.required'>Phone Number is required</div>
              <div class='error' v-if='!$v.order.phone_number.minLength'>Phone Number must be more than 6 numbers.</div>
              <div class='error' v-if='!$v.order.phone_number.maxLength'>Phone Number must be less than 255 numbers.</div>
            </div>
          </div>
        </section>

        <section class='shipping-address'>
          <h3>SHIPPING ADDRESS</h3>
          <OrderAddress @update='address_updated' :address='order.shipping_address' :$v='$v.order.shipping_address' />
          <div class='errors' style="position:relative; top:-2rem">
            <div v-if='errors.shipping_address' class='error'>{{ errors.shipping_address }}</div>
            <div v-if='errors.calculate_tax' class='error'>{{ errors.calculate_tax }}</div>
            <div v-if='errors.promo_code' class='error'>{{ errors.promo_code }}</div>
          </div>
        </section>

        <section v-if='order.shipping_method' class='shipping-method'>
          <h3>SHIPPING METHOD</h3>

          <div class='bold'>{{order.estimated_delivery}}</div>
          <div>{{order.shipping_method.name}} - ${{order.shipping_method.price_cents / 100}}</div>
        </section>

        <section v-if='+(amount_due).toFixed(2) > 0' class='payment-method'>
          <h3>PAYMENT METHOD</h3>
          <EditOrderPaymentMethod :order="order" :prices="prices" :admin="admin" ref="payment_method" />
        </section>

        <section class="checkboxes" v-if="order.pledge">
          <div>
            <label class='checkbox-label'>
              I agree to receive email notifications regarding product and feature updates, news, and promotions. View <a href='/privacy-policy' target='_blank'>Privacy Policy</a>.
              <input type='checkbox' name='subscribe' v-model='subscribe'>
              <span class="checkmark"></span>
            </label>
          </div>
        </section>

        <section v-if="order.confirm_preorder_email_sent">
          <p>
            By updating your pre-order, you are confirming that all the above information
            is correct. Since your Arsenal will be shipping out soon, you will not be able
            to make any further changes to your order.
          </p>
        </section>

        <section>
          <spinner-button :spin='submitting' class='btn btn-full btn-large'>Confirm {{ order.pledge ? 'Rewards' : 'Order' }}</spinner-button>
          <div class='terms-disclosures'>
            By clicking CONFIRM {{ order.pledge ? 'REWARDS' : 'ORDER' }}, you agree to the terms outlined in the {{ order.pledge ? 'rewards' : (order.preorder ? 'pre-order' : 'order') }} <a href='/terms-and-conditions' target='_blank'>Terms and Conditions</a>.
          </div>
          <br />
          <br />
        </section>
      </div>

      <div class='order-right'>
        <Totals :order='order' :subtotal='subtotal' :shipping='shipping' :tax='tax' :total='raw_total' :promo_code_discount='promo_code_discount' :prices='prices' :hide_pro='hide_pro' :hide_standard="hide_standard" />
      </div>
    </form>

    <OrderAddressModal :show='address_modal' :order='order' :cancel='close_address_modal' :submit_via_modal='submit_via_modal' />
  </div>
</template>

<script>
  import OrderAddress from 'app/components/orders/OrderAddress'
import PledgeProducts from 'app/components/orders/PledgeProducts'
import PreorderProducts from 'app/components/orders/PreorderProducts'
import Products from 'app/components/orders/Products'
import Totals from 'app/components/orders/Totals'
import { is_military_address, is_taxable_country, is_us_territory } from 'app/helpers/AddressHelpers'
import { noNumbers, noSpecialCharacters } from 'app/helpers/Validators'
import find from 'lodash/find'
import { email, maxLength, minLength, required } from 'vuelidate/lib/validators'
import EditOrderPaymentMethod from './EditOrderPaymentMethod.vue'

  import OrderAddressModal from 'app/components/orders/OrderAddressModal'
import SpinnerButton from 'app/components/shared/SpinnerButton'
import countries from 'app/countries.json'
import TotalsMixin from 'app/mixins/TotalsMixin'
import cloneDeep from 'lodash/cloneDeep'
import includes from 'lodash/includes'

  export default {
    mixins: [TotalsMixin],
    props: ['order', 'prices', 'estimated_delivery', 'hide_pro', 'hide_standard', 'hide_pro_upgrade', 'continental_usa', 'admin'],
    mounted() {
      if (includes(window.location.search, 'phone_number_required=true')) {
        this.$v.$touch()
        this.$toast("Please add your phone number to complete your preorder.", { horizontalPosition: 'center', verticalPosition: 'top' })
      }
    },
    data: () => {
      return {
        submitting: false,
        skip_address_validation: false,
        address_modal: false,
        agree_to_terms: false,
        agree_to_customs: false,
        agree_to_preorder: false,
        subscribe: false,
        errors: {}
      }
    },
    validations () {
      const obj = {
        order: {
          email: {
            required,
            email
          },
          phone_number: {
            required,
            minLength: minLength(6),
            maxLength: maxLength(255)
          },
          products: {
            arsenal: {
              cables: {
                $each: { required }
              }
            },
            arsenal_pro: {
              cables: {
                $each: { required }
              }
            },
          },
          shipping_address: {
            name: {
              required,
              maxLength: maxLength(100),
              noSpecialCharacters,
              noNumbers,
              hasFirstAndLastName() {
                if (!this.order.shipping_address.name) {
                  return true   // handled by `required`
                }
                return /\s/.test(this.order.shipping_address.name.trim())
              }
            },
            address_1: {
              required,
              maxLength: maxLength(35),
              noSpecialCharacters
            },
            address_2: {
              maxLength: maxLength(35),
              noSpecialCharacters
            },
            city: {
              required,
              maxLength: maxLength(255),
              noSpecialCharacters
            },
            state: {
              presence () {
                const country = find(countries, (country) => { return country.name == this.order.shipping_address.country })
                if (country.states.length === 0) return true
                return !!this.order.shipping_address.state
              }
            },
            zipcode: {
              presence () {
                const country = find(countries, (country) => { return country.name == this.order.shipping_address.country })
                if (country.postal_code === false) return true
                return !!this.order.shipping_address.zipcode
              },
              minLength: minLength(3),
              maxLength: maxLength(255),
              format () {
                // Only validate US States
                if (this.order.shipping_address.country != 'United States of America') return true
                if (is_us_territory(this.order.shipping_address.state)) return true
                if (is_military_address(this.order.shipping_address.state)) return true

                if (!this.order.shipping_address.zipcode) return false
                let numbers = this.order.shipping_address.zipcode.match(/\d/g).join('')
                return numbers.length == 5 || numbers.length == 9
              }
            }
          }
        },
        // agree_to_terms: {
        //   required () {
        //     if (!this.order.pledge) return true
        //     return this.agree_to_terms
        //   }
        // },
        // agree_to_customs: {
        //   required () {
        //     if (!this.order.pledge) return true
        //     if (!this.show_customs_checkbox) return true
        //     return this.agree_to_customs
        //   }
        // },
        // agree_to_preorder: {
        //   required() {
        //     if (!this.order.preorder) return true
        //     return this.agree_to_preorder
        //   }
        // }
      }

      if (this.order.pledge) {
        obj.order.products.pledge = {
          cables: {
            $each: { required }
          }
        }
      }

      return obj
    },
    computed: {
      show_customs_checkbox () {
        return !is_taxable_country(this.order.shipping_address.country)
      },
    },
    methods: {
      close_address_modal() {
        this.reset_form()
        this.address_modal = false
      },
      submit_via_modal() {
        this.address_modal = false
        this.skip_address_validation = true
        this.submitting = false
        this.validate_order()
      },
      address_updated() {
        this.update_tax()
      },
      update_tax(force_update = false) {
        if (!this.order.taxable) return

        if (this.$v.order.shipping_address.$invalid) {
          this.$set(this.order, 'tax_rate', 0)
          this.$set(this.order, 'shipping_taxable', false)
          return
        }

        let body = {
          taxable:  true,
          street:   this.order.shipping_address.address_1,
          city:     this.order.shipping_address.city,
          state:    this.order.shipping_address.state,
          zip:      this.order.shipping_address.zipcode,
          country:  this.order.shipping_address.country,
          subtotal: this.subtotal,
          shipping: this.shipping
        }

        if (JSON.stringify(body) != this.last_update_tax_request || force_update) {
          this.last_update_tax_request = JSON.stringify(body)
          this.$set(this.errors, 'shipping_address', null)

          this.$http.post('/orders/calculate_tax', body).then(response => {
            this.$set(this.order, 'tax_rate', response.body.tax_rate)
            this.$set(this.order, 'shipping_taxable', response.body.shipping_taxable)
          }, response => {
            this.$set(this.errors, 'shipping_address', response.body.error)
          })
        }
      },
      validate_order() {
        if (this.submitting) return
        this.submitting = true

        this.$v.$touch()

        if (this.order.tax_rate == null) {
          this.update_tax(true)
        }

        // Payment isn't always required on an edit,
        // so if there's no payment method, just submit the order
        // will validate server-side
        if (this.$refs.payment_method) {
          this.$refs.payment_method.create_token_if_needed((result, payment_method) => {
            if (payment_method == 'wallet') {
              this.skip_address_validation = true
            }

            if (result.canceled) {
              this.submitting = false
              return
            } else if (result.error) {
              const message = 'Some fields have errors. Please correct them and try again.'
              this.$toast(message, { horizontalPosition: 'center', verticalPosition: 'top' })
              this.reset_form()
              if (includes(this.order.products.arsenal.cables, '') || includes(this.order.products.arsenal_pro.cables, '')) {
                this.scroll_to_products()
              }
            } else if (result.token) {
              // new card or apple pay / google pay
              this.stripe_source_id = result.token.id
              this.card = result.token.card
              this.submit_order()
            } else {
              // charge the existing card
              this.submit_order()
            }
          })
        } else {
          // submit without payment
          this.submit_order()
        }
      },
      submit_order() {
        if (this.$v.$error) {
          const message = 'Some fields have errors. Please correct them and try again.'
          this.$toast(message, { horizontalPosition: 'center', verticalPosition: 'top' })
          this.reset_form()
          return
        }

        const body = {
          products:                cloneDeep(this.order.products),
          email:                   this.order.email,
          phone_number:            this.order.phone_number,
          shipping_name:           this.order.shipping_address.name,
          shipping_address_1:      this.order.shipping_address.address_1,
          shipping_address_2:      this.order.shipping_address.address_2,
          shipping_city:           this.order.shipping_address.city,
          shipping_country:        this.order.shipping_address.country,
          shipping_state:          this.order.shipping_address.state,
          shipping_zipcode:        this.order.shipping_address.zipcode,
          tax_rate:                this.order.tax_rate,
          shipping_taxable:        this.order.shipping_taxable,
          skip_address_validation: this.skip_address_validation,
          pricing:                 this.order.product_prices,
        }
        if (body.products.pledge) {
          if (body.products.pledge.arsenal_model == 'Arsenal 2 Standard') {
            body.products.arsenal.quantity += body.products.pledge.quantity
            body.products.arsenal.cables = body.products.arsenal.cables.concat(body.products.pledge.cables)
          } else if (body.products.pledge.arsenal_model == 'Arsenal 2 Pro') {
            body.products.arsenal_pro.quantity += body.products.pledge.quantity
            body.products.arsenal_pro.cables = body.products.arsenal_pro.cables.concat(body.products.pledge.cables)
          }
        }

        if (this.stripe_source_id) {
          body.stripe_source_id = this.stripe_source_id
          body.card_exp_month   = this.card.exp_month
          body.card_exp_year    = this.card.exp_year
          body.card_last_4      = this.card.last4
          body.card_brand       = this.card.brand
        }

        this.$http.put(`/orders/${this.order.uid}`, body).then(response => {
          this.submitting = false

          Intercom('trackEvent', 'order-updated', {
            order_id: response.body.id,
            total: `$${response.body.total}`,
            link: `${window.location.origin}/order/${response.body.uid}`
          })

          if (this.admin) {
            window.location = `/admin/orders/${this.order.uid}?from_update=true`
          } else if (this.order.pledge) {
            window.location = `/success?pledge_confirmed=true`
          } else if (this.order.can_edit_preorder) {
            window.location = `/success?preorder_confirmed=true`
          } else {
            window.location = `/order/${this.order.uid}?from_update=true`
          }
        }, response => {
          if (response.status === 422) {
            this.address_modal = true
          } else if (response.status === 401) {
            this.reset_form()
            let error = 'Your order is either shipped or about to be shipped and can no longer be updated.'
            this.$toast(error, { horizontalPosition: 'center', verticalPosition: 'top' })
          } else {
            this.reset_form()
            let error = response.body.error || 'Something went wrong.  Please try again or contact support.'
            this.$toast(error, { horizontalPosition: 'center', verticalPosition: 'top' })
          }
        })
      },
      scroll_to_products() {
        this.$el.scrollIntoView({ behavior: 'smooth' })
      },
      reset_form() {
        this.submitting = false
        this.skip_address_validation = false
      },
      select_payment_method(method) {
        this.payment_method = method
      },
    },
    components: { Totals, Products, PledgeProducts, PreorderProducts, OrderAddress, EditOrderPaymentMethod, SpinnerButton, OrderAddressModal }
  }
</script>
